import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Cpu } from 'lucide-react';
import Header from './Header';

const CaffryxHero = ({ mousePos, isHovered, setIsHovered }) => {
  return (
    <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden px-6">
      {/* --- MAIN CONTENT --- */}
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col items-center text-center">
        
        {/* Status Badge */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex items-center gap-3 bg-white/80 backdrop-blur-md border border-[#2B6CB0]/10 px-5 py-2 rounded-full shadow-sm"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2B6CB0] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2B6CB0]"></span>
          </span>
          <span className="text-[#2B6CB0] text-[10px] font-black uppercase tracking-[0.3em]">
            Engineering the Future 
          </span>
        </motion.div>

        {/* Headline with Mask Animation */}
        <div className="relative mb-8">
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-[#020617] text-6xl md:text-[120px] font-black leading-[0.85] uppercase tracking-tighter"
          >
            Caffryx <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2B6CB0] to-[#4A5568]">
              Experience.
            </span>
          </motion.h1>

          {/* Floating Floating Icon 1 */}
          <motion.div 
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-12 -right-12 text-[#2B6CB0]/20 hidden lg:block"
          >
            <Cpu size={100} strokeWidth={1} />
          </motion.div>
        </div>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="max-w-2xl text-[#4A5568] text-lg md:text-xl font-medium leading-relaxed mb-12"
        >
          Engineering high-performance web solutions and content-driven social strategies for the modern brand.
        </motion.p>

        {/* CTA Group */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center gap-6"
        >
          <motion.button
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group relative bg-[#2B6CB0] text-white px-10 py-5 rounded-xl font-bold uppercase tracking-widest text-xs overflow-hidden transition-shadow hover:shadow-2xl hover:shadow-[#2B6CB0]/30"
          >
            <span className="relative z-10 flex items-center gap-3">
              Deploy Your Vision <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </span>
            <AnimatePresence>
              {isHovered && (
                <motion.div
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  exit={{ x: '100%' }}
                  transition={{ duration: 0.6 }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent z-0"
                />
              )}
            </AnimatePresence>
          </motion.button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-10 flex flex-col items-center gap-3 opacity-40"
      >
        <div className="w-[1px] h-12 bg-[#4A5568]" />
        <span className="text-[#4A5568] text-[9px] uppercase tracking-[0.4em] [writing-mode:vertical-lr]">Discover</span>
      </motion.div>
    </section>
  );
};

function CaffryxHome() {
  const [activeWork, setActiveWork] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const scrollRef = useRef(null);

  // Global mouse tracking for the parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const workItems = useMemo(() => [
    { title: "Custom Web Development", category: "E-Commerce", img: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&q=80&w=1200" },
    { title: "Data Analytics", category: "Strategy", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200" },
    { title: "Social Media", category: "Management", img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&q=80&w=1200" },
    { title: "Professional Video & Pic", category: "Production", img: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&q=80&w=1200" },
    { title: "SEO Optimization", category: "Visibility", img: "https://images.unsplash.com/photo-1571171637578-41bc2dd41cd2?auto=format&fit=crop&q=80&w=1200" },
  ], []);

  const services = useMemo(() => [
    { title: 'Web Development', tag: 'Engineering', desc: 'Custom-coded eCommerce and corporate sites built with React for ultimate speed, security, and SEO performance.' },
    { title: 'Visual Branding & Social', tag: 'Management', desc: 'Complete brand systems paired with full-service Social Media Management.' },
    { title: 'Photo & Video Packages', tag: 'Production', desc: 'Professional photography and video production tailored for high-conversion reels and ads.' },
    { title: 'Weekly Posting Packages', tag: 'Consistency', desc: 'Turnkey weekly posting strategies designed to keep your brand top-of-mind effortlessly.' },
    { title: 'Cloud Solutions', tag: 'Infrastructure', desc: 'Secure domain procurement and enterprise cloud hosting with 24/7 monitoring.' },
    { title: 'Performance SEO', tag: 'Visibility', desc: 'Data-driven search engine optimization to scale your organic reach and conversion.' }
  ], []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveWork((prev) => (prev + 1) % workItems.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [workItems.length]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative overflow-x-hidden bg-[#F0F4F8] text-[#4A5568] antialiased font-sans">
      <Header />
      
      {/* --- GLOBAL DYNAMIC BACKGROUND (NOW FULL PAGE) --- */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {/* Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.15]" 
             style={{ backgroundImage: `radial-gradient(#2B6CB0 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />
        
        {/* Floating Interactive Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                x: mousePos.x * (i + 1) * 40,
                y: mousePos.y * (i + 1) * 40,
                rotate: mousePos.x * 45,
              }}
              transition={{ type: 'spring', stiffness: 40, damping: 20 }}
              className="absolute border border-[#2B6CB0]/20 rounded-2xl"
              style={{
                width: `${(i + 1) * 250}px`,
                height: `${(i + 1) * 250}px`,
                left: `${20 + i * 20}%`,
                top: `${15 + i * 20}%`,
              }}
            />
          ))}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .marquee-text { animation: marquee 40s linear infinite; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />

      {/* Hero Section - Now transparent to show global background */}
      <CaffryxHero mousePos={mousePos} isHovered={isHovered} setIsHovered={setIsHovered} />

      {/* --- SERVICES MARQUEE --- */}
      <div className="relative z-10 bg-[#020617] py-8 overflow-hidden whitespace-nowrap border-y border-[#2B6CB0]/30">
        <div className="marquee-text inline-block text-white font-black uppercase tracking-[0.5em] text-[10px]">
          WEEKLY POSTING • PHOTO & VIDEO • SOCIAL MANAGEMENT • CUSTOM CODE • SEO STRATEGY • BRAND IDENTITY •&nbsp;
          WEEKLY POSTING • PHOTO & VIDEO • SOCIAL MANAGEMENT • CUSTOM CODE • SEO STRATEGY • BRAND IDENTITY •&nbsp;
        </div>
      </div>

      {/* --- CASE STUDIES --- */}
      <section className="relative z-10 py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            
            <div className="relative h-[600px] w-full rounded-[2rem] overflow-hidden shadow-2xl border-[12px] border-white group bg-white order-2 lg:order-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeWork}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  className="absolute inset-0"
                >
                  <img 
                    src={workItems[activeWork].img} 
                    alt={workItems[activeWork].title} 
                    className="w-full h-full object-cover" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/60 to-transparent" />
                  <div className="absolute bottom-12 left-12 text-white text-left drop-shadow-lg">
                    <p className="text-[10px] font-black uppercase tracking-[0.4em] mb-4 text-[#2B6CB0]">{workItems[activeWork].category}</p>
                    <h3 className="text-4xl font-black uppercase tracking-tighter">{workItems[activeWork].title}</h3>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="space-y-8 order-1 lg:order-2 text-left">
              <p className="text-[#2B6CB0] font-black text-[10px] uppercase tracking-[0.4em]">Case Studies</p>
              <h2 className="text-5xl md:text-8xl text-[#020617] font-black tracking-tighter uppercase leading-[0.85]">
                Digital <br/> <span className="text-[#2B6CB0]">Identities.</span>
              </h2>
              <p className="text-xl text-[#4A5568] leading-relaxed font-medium">
                We bridge the gap between <span className="text-[#020617] font-black underline decoration-[#2B6CB0] decoration-4">Technical Engineering</span> and <span className="text-[#020617] font-black underline decoration-[#2B6CB0] decoration-4">Social Influence</span>.
              </p>

              <div className="flex flex-col gap-3 py-4 border-l-2 border-[#2B6CB0]/10 pl-6">
                {workItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveWork(index)}
                    className={`text-left uppercase tracking-widest text-[10px] font-black transition-all duration-300 ${
                      activeWork === index ? 'text-[#2B6CB0] translate-x-3' : 'text-[#4A5568]/40 hover:text-[#020617]'
                    }`}
                  >
                    0{index + 1} — {item.title}
                  </button>
                ))}
              </div>

              <Link to="/work" className="inline-block bg-[#2B6CB0] text-white px-10 py-5 rounded-xl font-black uppercase tracking-[0.2em] text-[11px] hover:bg-[#020617] transition-all shadow-xl shadow-[#2B6CB0]/20">
                Explore Portfolio
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* --- FULL SCALE SOLUTIONS --- */}
      <section className="relative z-10 py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
            <div className="text-left">
              <h2 className="text-5xl md:text-8xl text-[#020617] font-black uppercase tracking-tighter leading-[0.85] mb-6">
                Full Scale <br/> <span className="text-[#2B6CB0]">Solutions</span>
              </h2>
            </div>
            
            <div className="flex gap-4">
              <button aria-label="Scroll Left" onClick={() => scroll('left')} className="w-14 h-14 rounded-full border border-[#4A5568]/20 bg-white/50 backdrop-blur-sm flex items-center justify-center hover:bg-[#2B6CB0] hover:text-white transition-all group">
                <span className="text-2xl group-hover:-translate-x-1 transition-transform">←</span>
              </button>
              <button aria-label="Scroll Right" onClick={() => scroll('right')} className="w-14 h-14 rounded-full border border-[#4A5568]/20 bg-white/50 backdrop-blur-sm flex items-center justify-center hover:bg-[#2B6CB0] hover:text-white transition-all group">
                <span className="text-2xl group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex gap-6 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-8">
            {services.map((s, i) => (
              <div key={i} className="min-w-[85vw] md:min-w-[420px] snap-center bg-white/60 backdrop-blur-md p-12 rounded-[2rem] flex flex-col justify-between h-[480px] border border-[#4A5568]/5 hover:border-[#2B6CB0]/30 transition-all group shadow-sm">
                <div className="text-left">
                  <span className="text-[10px] font-black uppercase text-[#2B6CB0] tracking-[0.3em] mb-6 block">{s.tag}</span>
                  <h3 className="text-3xl font-black uppercase tracking-tighter text-[#020617] mb-6 leading-none">{s.title}</h3>
                  <p className="text-[#4A5568] text-base leading-relaxed font-medium">{s.desc}</p>
                </div>
                <Link to="/services" className="text-[#020617] font-black text-[11px] uppercase tracking-widest border-b-2 border-[#2B6CB0] w-fit pb-1 group-hover:text-[#2B6CB0] transition-colors">
                  Explore Service
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- FINAL CTA --- */}
      <section className="relative z-10 py-24 bg-[#020617]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="text-5xl md:text-8xl font-black text-white uppercase tracking-tighter leading-tight mb-16">
            Level <span className="text-[#2B6CB0]">Up.</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="tel:8326176250" className="group bg-white/5 border border-white/10 p-8 rounded-xl flex flex-col items-center gap-4 hover:border-[#2B6CB0]/50 transition-all">
              <div className="text-[#2B6CB0]">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <span className="text-white font-bold uppercase tracking-widest text-[10px]">Call Us</span>
            </a>
            <Link to="/contact" className="bg-[#2B6CB0] p-8 rounded-xl flex flex-col items-center justify-center shadow-xl shadow-[#2B6CB0]/20 hover:scale-[1.02] transition-transform">
              <span className="text-white font-black uppercase tracking-widest text-xs">Contact Us</span>
            </Link>
            <a href="mailto:caffryx@gmail.com" className="group bg-white/5 border border-white/10 p-8 rounded-xl flex flex-col items-center gap-4 hover:border-[#2B6CB0]/50 transition-all">
              <div className="text-[#2B6CB0]">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-white font-bold uppercase tracking-widest text-[10px]">Email Us</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CaffryxHome;